'use client';

import { useCallback, useContext, useSyncExternalStore } from 'react';
import { QueryClientContext, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { HdhpNoticeData, RegistrationProfileView } from '@galapagos/shared';
import type { SignupProgramOptions } from '@galapagos/shared/registration';
import { ProgressBar } from '@galapagos/design-system/web';
import { readLinkIncentive } from '@/services/link-incentive';
import { showsPlanDetails } from '../health-plan/plan-details';
import { signupProgramOptionsQueryKey } from '../hooks/useSignupProgramOptions';
import { signupSponsorQueryKey } from '../hooks/useSignupSponsor';
import { parseLegacySignupParams } from '../services/legacy-params';
import type { SignupSponsor } from '../services/signup-api/signup-api';
import { signupProgressFor, signupStepForPathname } from '../signup-progress';
import { signupPathFor } from '../steps';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { selectedProgramsUnavailable, toWrapUpContext } from '../wrap-up/branching';
import { hdhpNoticeQueryKey, registrationProfileQueryKey } from '../wrap-up/useWrapUpContext';

/**
 * Reads, without fetching, what the flow has already loaded about this member
 * and works out how full the bar is. The location is the page and the link;
 * the sponsor, HDHP notice, profile and program options are the answers the
 * steps have already read, under the same keys those steps read them by; the
 * flow store holds the member's own choices. An answer not loaded yet counts
 * as the usual route, exactly as the wrap-up routes on it.
 */
function readProgress(queryClient: QueryClient | undefined): number | null {
  const step = signupStepForPathname(window.location.pathname);
  if (step === null) return null;
  const search = window.location.search;
  const params = parseLegacySignupParams(search);
  const path = signupPathFor(params);
  const scenario = new URLSearchParams(search).get('scenario');
  const { sponsorRef, programSelection } = useSignupFlowStore.getState();
  const sponsor = queryClient?.getQueryData<SignupSponsor>(
    path === 'no-employer'
      ? signupSponsorQueryKey(scenario, sponsorRef ?? undefined, undefined)
      : signupSponsorQueryKey(scenario, params.clientId, params.subClientId),
  );
  const hdhp = queryClient?.getQueryData<HdhpNoticeData>(hdhpNoticeQueryKey(scenario));
  const profile = queryClient?.getQueryData<RegistrationProfileView>(
    registrationProfileQueryKey(scenario),
  );
  const options = queryClient?.getQueryData<SignupProgramOptions>(
    signupProgramOptionsQueryKey(scenario),
  );
  const programsUnavailable = selectedProgramsUnavailable(
    options?.options ?? [],
    programSelection,
    profile?.ineligibleProgramKeys ?? [],
  );
  return signupProgressFor(step, path, {
    planDetails: showsPlanDetails({ healthPlan: sponsor?.features.healthPlan, flow: params.flow }),
    wrapUp: toWrapUpContext(
      sponsor?.features,
      hdhp?.requiresHdhpNotice,
      profile?.minorStatus,
      programsUnavailable,
      readLinkIncentive(search),
    ),
  });
}

const noProgressOnServer = (): number | null => null;

/**
 * The header's progress bar (v9 Site header): how far the member is along
 * the steps they will see on their own path. The answers it reads only arrive
 * in the browser, so the prerendered page draws no bar; the bar redraws when
 * a step loads a new answer or the member makes a choice. The location is
 * read directly, which keeps the frame free of router hooks. Pages that are
 * not a step on the member's path render nothing, so the header slot stays
 * empty and hidden.
 */
export function SignupProgress() {
  const { t } = useTranslation('signup');
  const queryClient = useContext(QueryClientContext);
  const subscribe = useCallback(
    (onChange: () => void) => {
      const unsubscribeQueries = queryClient?.getQueryCache().subscribe(onChange);
      const unsubscribeStore = useSignupFlowStore.subscribe(onChange);
      return () => {
        unsubscribeQueries?.();
        unsubscribeStore();
      };
    },
    [queryClient],
  );
  const read = useCallback(() => readProgress(queryClient), [queryClient]);
  const value = useSyncExternalStore(subscribe, read, noProgressOnServer);
  if (value === null) return null;
  return (
    <ProgressBar
      value={value}
      size="slim"
      tone="contrast"
      accessibilityLabel={t('header.progressLabel')}
      data-testid="signup-header-progress-bar"
    />
  );
}
